/**
 * Concierge routes (phase 1, 10 §3). Guest: the web chat on `/g/concierge`. Staff: the case drawer,
 * instruct / take / hand back / reply on a lead, agent settings and grants (owner only), Try it, and
 * the AI cost report. Kept apart from the phase-0 tables so their fakes stay complete; iterate them
 * with `flattenGuestRoutes()` and `flattenStaffAgentRoutes()`.
 */
import { z } from 'zod';
import { AgentSettings, AgentSettingsInput, AiCostReport, CaseEvent, CaseSummary, ConciergeMessage, ConciergeState, EvalCandidate, EvalCandidateReason, GuestScene, ToolGrant, ToolGrantInput, TryItSession, } from '../agent.js';
import { DateString, Id, IdParams, listOf } from '../common.js';
import { Message, StaffNote } from '../leads.js';
import { PlanInput } from '../quote.js';
import { flattenRoutes } from '../routes.js';
import { guestRoutes } from './guest.js';
import { StaffLead } from './staff.js';
// ── Guest ─────────────────────────────────────────────────────────────────
export const ConciergeSendBody = z.object({
    text: z.string().trim().min(1).max(4000),
    /** The scene the guest is on. */
    scene: GuestScene.optional(),
    /** Client-generated; a retried POST with the same id is not answered twice. */
    clientMessageId: Id,
});
export const ConciergeHistory = z.object({
    /** Newest last. */
    messages: z.array(ConciergeMessage),
    state: ConciergeState,
});
export const guestAgentRoutes = {
    /**
     * Sends a guest message and streams the answer as `ConciergeSseEvent`s. Runs the turn inline (60 s
     * wall clock); a second message on the same lead waits behind the first.
     */
    conciergeSend: {
        method: 'POST',
        path: '/g/concierge',
        body: ConciergeSendBody,
        response: null,
        status: 200,
        sse: true,
        auth: 'token',
    },
    conciergeHistory: {
        method: 'GET',
        path: '/g/concierge',
        response: ConciergeHistory,
        status: 200,
        auth: 'token',
    },
};
/** Every guest route, phase 0 and concierge, as `['venue', def]` pairs. */
export function flattenGuestRoutes() {
    return [...flattenRoutes(guestRoutes), ...flattenRoutes(guestAgentRoutes)];
}
// ── Staff ─────────────────────────────────────────────────────────────────
export const CaseQuery = z.object({
    /** Events with `seq` greater than this (paging, or polling while a turn runs). */
    afterSeq: z.coerce.number().int().min(-1).default(-1),
    limit: z.coerce.number().int().min(1).max(500).default(200),
});
/** The lead's latest case (null if the agent never ran on it) and a page of its ledger, by `seq`. */
export const LeadCase = z.object({
    case: CaseSummary.nullable(),
    events: z.array(CaseEvent),
    hasMore: z.boolean(),
});
export const StaffTextBody = z.object({ text: z.string().trim().min(1).max(10_000) });
/** Replaces the listed tool × channel grants; grants not listed keep their level. */
export const PutGrantsBody = z.object({ grants: z.array(ToolGrantInput).min(1).max(200) });
export const CreateTryItBody = z.object({
    /** A persona preset's plan. */
    plan: PlanInput.optional(),
});
export const FlagTurnBody = z.object({
    eventSeq: z.number().int().min(0),
    reason: EvalCandidateReason,
    note: z.string().trim().max(2000).default(''),
});
export const AiCostQuery = z.object({ from: DateString, to: DateString });
const actors = ['owner', 'coordinator'];
const ownerOnly = ['owner'];
export const staffAgentRoutes = {
    leadAgent: {
        case: {
            method: 'GET',
            path: '/s/leads/:id/case',
            params: IdParams,
            query: CaseQuery,
            response: LeadCase,
            status: 200,
            auth: 'staff',
        },
        /** A staff instruction (a `StaffNote` of kind `instruction`); resumes the case with `staff_input`. */
        instruct: {
            method: 'POST',
            path: '/s/leads/:id/instruct',
            params: IdParams,
            body: StaffTextBody,
            response: StaffNote,
            status: 201,
            auth: 'staff',
            roles: actors,
        },
        /** Sets `ownerStaffId` to the caller; the agent stays silent on this lead until handed back. */
        take: {
            method: 'POST',
            path: '/s/leads/:id/take',
            params: IdParams,
            response: StaffLead,
            status: 200,
            auth: 'staff',
            roles: actors,
        },
        /** Clears `ownerStaffId` and resumes the case with `handed_back`. */
        handback: {
            method: 'POST',
            path: '/s/leads/:id/handback',
            params: IdParams,
            response: StaffLead,
            status: 200,
            auth: 'staff',
            roles: actors,
        },
        /** Staff speaks on the web channel; 409 CONFLICT unless the caller owns the lead (`take` first). */
        reply: {
            method: 'POST',
            path: '/s/leads/:id/reply',
            params: IdParams,
            body: StaffTextBody,
            response: Message,
            status: 201,
            auth: 'staff',
            roles: actors,
        },
    },
    agentSettings: {
        get: { method: 'GET', path: '/s/agent/settings', response: AgentSettings, status: 200, auth: 'staff' },
        put: {
            method: 'PUT',
            path: '/s/agent/settings',
            body: AgentSettingsInput,
            response: AgentSettings,
            status: 200,
            auth: 'staff',
            roles: ownerOnly,
        },
    },
    agentGrants: {
        list: { method: 'GET', path: '/s/agent/grants', response: listOf(ToolGrant), status: 200, auth: 'staff' },
        put: {
            method: 'PUT',
            path: '/s/agent/grants',
            body: PutGrantsBody,
            response: listOf(ToolGrant),
            status: 200,
            auth: 'staff',
            roles: ownerOnly,
        },
    },
    tryIt: {
        /** A synthetic lead: `send:web` runs (only staff read it); every other write is `observe`. */
        create: {
            method: 'POST',
            path: '/s/try-it/sessions',
            body: CreateTryItBody,
            response: TryItSession,
            status: 201,
            auth: 'staff',
            roles: actors,
        },
        /** Deletes the session and its synthetic lead. */
        delete: {
            method: 'DELETE',
            path: '/s/try-it/sessions/:id',
            params: IdParams,
            response: null,
            status: 204,
            auth: 'staff',
            roles: actors,
        },
        /** Flags one ledger event of the session's case as an eval candidate. */
        flag: {
            method: 'POST',
            path: '/s/try-it/sessions/:id/flag',
            params: IdParams,
            body: FlagTurnBody,
            response: EvalCandidate,
            status: 201,
            auth: 'staff',
            roles: actors,
        },
    },
    reports: {
        aiCost: {
            method: 'GET',
            path: '/s/reports/ai-cost',
            query: AiCostQuery,
            response: AiCostReport,
            status: 200,
            auth: 'staff',
        },
    },
};
/** Every concierge staff route as `['leadAgent.take', def]` pairs. */
export function flattenStaffAgentRoutes() {
    return flattenRoutes(staffAgentRoutes);
}
//# sourceMappingURL=agent.js.map